import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { businessSupplierService, businessPublicService, businessMappingService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import StepCell from '../Checklist/StepCell';
import StepEditorModal from '../Checklist/StepEditorModal';
import SupplierFormModal from './SupplierFormModal';
import AddToPlantModal from './AddToPlantModal';
import '../../../styles/businessTheme.css';
import '../Checklist/Checklist.css';

export default function SupplierDetail() {
  const { id } = useParams();
  const { isBusinessLead } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [allPlants, setAllPlants] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showEditSupplier, setShowEditSupplier] = useState(false);
  const [showAddToPlant, setShowAddToPlant] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    const [supplierRes, mappingsRes] = await Promise.all([
      businessSupplierService.getById(id),
      businessSupplierService.getMappings(id),
    ]);
    setSupplier(supplierRes.data.supplier);
    setMappings(mappingsRes.data.mappings);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { businessPublicService.listPlants().then(res => setAllPlants(res.data.plants)); }, []);

  if (!supplier) return null;

  const mappedPlantIds = new Set(mappings.map(m => String(m.plant._id)));
  const availablePlants = allPlants.filter(p => !mappedPlantIds.has(String(p._id)));

  const handleRemove = async (mapping) => {
    if (!window.confirm(`Remove ${supplier.name} from ${mapping.plant.name}? Its onboarding checklist for this plant will be deleted.`)) return;
    setRemoving(mapping._id);
    try {
      await businessMappingService.remove(mapping._id);
      await load();
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="checklist-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <Link to="/business/suppliers" style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
            <ArrowLeft size={12} /> Back to suppliers
          </Link>
          <h1 className="page-title">{supplier.name}</h1>
          <p className="page-subtitle">
            Code {supplier.vendorCode} · {mappings.length} plant{mappings.length !== 1 ? 's' : ''}
            {supplier.contactEmail ? ` · ${supplier.contactEmail}` : ''}
            {supplier.contactPhone ? ` · ${supplier.contactPhone}` : ''}
          </p>
        </div>
        {isBusinessLead && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setShowEditSupplier(true)}>
              <Pencil size={14} /> Edit
            </button>
            <button className="btn btn-primary" onClick={() => setShowAddToPlant(true)}>
              <Plus size={14} /> Add to Plant
            </button>
          </div>
        )}
      </div>

      {mappings.map(m => (
        <div key={m._id} className="supplier-plant-section card checklist-table-wrap checklist-table">
          <div className="supplier-plant-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{m.plant.name} ({m.plant.code})</span>
            {isBusinessLead && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="icon-btn-sm" title="Edit plant details" onClick={() => setEditingMapping(m)}>
                  <Pencil size={14} />
                </button>
                <button className="icon-btn-sm" title="Remove from plant" disabled={removing === m._id} onClick={() => handleRemove(m)}>
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
          <table>
            <thead>
              <tr>
                {m.steps.map(entry => <th key={entry._id}>{entry.step.name}</th>)}
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {m.steps.map(entry => (
                  <StepCell
                    key={entry._id}
                    entry={entry}
                    editable={isBusinessLead}
                    onClick={() => setEditing({ mapping: m, entry })}
                  />
                ))}
                <td className="checklist-notes-cell">{m.notes || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {mappings.length === 0 && <div className="card empty-state">This supplier is not mapped to any plant yet</div>}

      {editing && (
        <StepEditorModal
          mappingId={editing.mapping._id}
          entry={editing.entry}
          stepName={editing.entry.step.name}
          supplierName={supplier.name}
          plantName={editing.mapping.plant.name}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {showEditSupplier && (
        <SupplierFormModal
          supplier={supplier}
          onClose={() => setShowEditSupplier(false)}
          onSaved={() => { setShowEditSupplier(false); load(); }}
        />
      )}

      {showAddToPlant && (
        <AddToPlantModal
          supplierId={supplier._id}
          supplierName={supplier.name}
          availablePlants={availablePlants}
          onClose={() => setShowAddToPlant(false)}
          onSaved={() => { setShowAddToPlant(false); load(); }}
        />
      )}

      {editingMapping && (
        <AddToPlantModal
          supplierId={supplier._id}
          supplierName={supplier.name}
          mapping={editingMapping}
          onClose={() => setEditingMapping(null)}
          onSaved={() => { setEditingMapping(null); load(); }}
        />
      )}
    </div>
  );
}
